import * as React from "react"
import { DayPicker, DropdownProps } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0 relative",
        month: "space-y-4 px-2 pb-2",
        month_caption: "flex justify-center pt-1 relative items-center h-9",
        caption_label: "hidden", // Hide label when dropdowns are active
        nav: "absolute top-2 w-full flex justify-between px-2 h-0",
        button_previous: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-1 top-2 z-20"
        ),
        button_next: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-1 top-2 z-20"
        ),
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday:
          "text-muted-foreground rounded-md w-11 h-10 flex items-center justify-center font-normal text-[0.8rem]",
        week: "flex w-full mt-2 gap-1",
        day: "h-10 w-11 text-center text-sm p-0 relative focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost" }),
          "h-10 w-11 p-0 font-normal aria-selected:opacity-100 hover:bg-accent hover:text-accent-foreground rounded-md"
        ),
        range_end: "day-range-end",
        selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        today: "bg-accent text-accent-foreground font-bold",
        outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        dropdowns: "flex justify-center gap-2 items-center mx-1 z-20 relative",
        dropdown: "rdp-dropdown",
        dropdown_month: "font-medium",
        dropdown_year: "font-medium",
        ...classNames,
      }}
      components={{
        Dropdown: ({ value, options, onChange }: DropdownProps) => {
          return (
            <Select
              value={value?.toString()}
              onValueChange={(newValue) => {
                const event = {
                  target: { value: newValue },
                } as React.ChangeEvent<HTMLSelectElement>
                onChange?.(event)
              }}
            >
              <SelectTrigger className="h-8 w-fit gap-1 bg-secondary/50 hover:bg-secondary border-none shadow-sm focus:ring-0 px-2 py-1 font-semibold text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent position="popper" className="max-h-[250px] overflow-y-auto">
                {options?.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value.toString()}
                    className="text-xs"
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        },
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
 